import React, { useState } from 'react'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useSelector } from 'react-redux'
import { State } from '../types/state'
import { Button, Fab, Paper, Box } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import NewOrEditContractorDialog from './NewOrEditContractorDialog'
import { Contractor } from '../types/contractor'
import DeleteContractorDialog from './DeleteContractorDialog'
import GenerateContractorReceiptDialog from './GenerateContractorReceiptDialog'
import { useIsMobileView } from '../hooks/useIsMobileView'
import { modernCard } from '../styles/modern'
import { typographyStyles } from '../styles/typography'

const Contractors = (): JSX.Element => {
  const { contractors } = useSelector((state: State) => state.user)
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([])
  const [selectedRowIdsToDelete, setSelectedRowIdsToDelete] = useState<
  number[]
  >([])
  const [contractor, setContractor] = useState<Contractor | null>(null)
  const [generateReceiptContractorId, setGenerateReceiptContractorId] =
    useState<number | null>(null)
  const [isNewOrEditContractorDialogOpen, setIsNewOrEditContractorDialogOpen] =
    useState(false)
  const [isDeleteContractorDialogOpen, setIsDeleteContractorDialogOpen] =
    useState(false)
  const [
    isGenerateContractorReceiptDialogOpen,
    setIsGenerateContractorReceiptDialogOpen
  ] = useState(false)
  const isMobileView = useIsMobileView()

  const OptionsButton: React.FC<{ id: number }> = ({ id }) => {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Fab
          size='small'
          onClick={() => {
            setContractor(contractors.find(c => c.id === id) as any)
            setIsNewOrEditContractorDialogOpen(true)
          }}
          sx={{
            background: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
            color: 'white',
            transform: 'scale(0.85)',
            boxShadow: '0 4px 10px rgba(237, 137, 54, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #ed8936 0%, #f6ad55 100%)',
              transform: 'scale(0.9)'
            }
          }}
          aria-label='edit'
        >
          <EditIcon />
        </Fab>
        <Fab
          size='small'
          onClick={() => {
            setSelectedRowIdsToDelete([id])
            setIsDeleteContractorDialogOpen(true)
          }}
          sx={{
            background: 'linear-gradient(135deg, #fc8181 0%, #f56565 100%)',
            color: 'white',
            transform: 'scale(0.85)',
            boxShadow: '0 4px 10px rgba(245, 101, 101, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #f56565 0%, #fc8181 100%)',
              transform: 'scale(0.9)'
            }
          }}
          aria-label='delete'
        >
          <DeleteIcon />
        </Fab>
        <Button
          variant='contained'
          style={{
            fontSize: 12,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 10px rgba(102, 126, 234, 0.3)',
            padding: '6px 16px'
          }}
          onClick={() => {
            setGenerateReceiptContractorId(id)
            setIsGenerateContractorReceiptDialogOpen(true)
          }}
        >
          Generate receipt
        </Button>
      </Box>
    )
  }

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 110 },
    { field: 'taxId', headerName: 'Tax id', width: 90 },
    { field: 'city', headerName: 'City', width: 90 },
    { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 130 },
    { field: 'phoneNumber', headerName: 'Phone number', width: 110 },
    {
      field: 'options',
      headerName: '',
      flex: 1.8,
      minWidth: 260,
      renderCell: params => <OptionsButton id={params.row.id} />
    }
  ]

  return (
    <Box
      sx={{
        width: isMobileView ? '85vw' : '80vw',
        margin: 'auto',
        marginBottom: '40px',
        padding: isMobileView ? '20px' : '30px'
      }}
    >
      <Paper elevation={0} style={modernCard} className='modern-card'>
        <h2 style={{ ...typographyStyles.h2, marginBottom: '24px', textAlign: 'center' }}>
          Contractors Management
        </h2>
        <Box sx={{ display: 'flex', gap: 2, marginBottom: '20px', flexWrap: 'wrap' }}>
          <Fab
            size='medium'
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                transform: 'scale(1.05)'
              }
            }}
            aria-label='add'
            onClick={() => {
              setContractor(null)
              setIsNewOrEditContractorDialogOpen(true)
            }}
            disabled
          >
            <AddIcon />
          </Fab>
          <Fab
            size='medium'
            onClick={() => {
              setSelectedRowIdsToDelete(selectedRowIds)
              setIsDeleteContractorDialogOpen(true)
            }}
            sx={{
              background: 'linear-gradient(135deg, #fc8181 0%, #f56565 100%)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(245, 101, 101, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #f56565 0%, #fc8181 100%)',
                transform: 'scale(1.05)'
              }
            }}
            aria-label='delete'
            disabled={selectedRowIds.length === 0 || true}
          >
            <DeleteIcon />
          </Fab>
        </Box>
        <Box sx={{ height: isMobileView ? '60vh' : '55vh', width: '100%', overflow: 'hidden' }}>
          <DataGrid
            disableRowSelectionOnClick
            rows={contractors}
            columns={columns}
            checkboxSelection
            onRowSelectionModelChange={newSelection => {
              setSelectedRowIds(newSelection as number[])
            }}
            sx={{
              border: 'none',
              width: '100%',
              '& .MuiDataGrid-cell:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.08)'
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '8px 8px 0 0',
                fontWeight: 600
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.05)'
              }
            }}
          />
        </Box>
      </Paper>
      {isNewOrEditContractorDialogOpen && (
        <NewOrEditContractorDialog
          handleClose={() => setIsNewOrEditContractorDialogOpen(false)}
          contractor={contractor}
        />
      )}
      {isDeleteContractorDialogOpen && (
        <DeleteContractorDialog
          handleClose={() => setIsDeleteContractorDialogOpen(false)}
          ids={selectedRowIdsToDelete}
        />
      )}
      {isGenerateContractorReceiptDialogOpen &&
        generateReceiptContractorId !== null && (
          <GenerateContractorReceiptDialog
            handleClose={() => setIsGenerateContractorReceiptDialogOpen(false)}
            contractorId={generateReceiptContractorId}
          />
      )}
    </Box>
  )
}

export default Contractors
